import { useEffect, useState, type FormEvent } from "react";
import { Building2, CheckCircle2, Save, ShieldCheck, Sparkles, Target } from "lucide-react";
import type { ConstraintSet, InstitutionProfile } from "../../shared/types";
import { Badge, Button, Card, SectionHeader, Spinner } from "../components/Ui";
import { api } from "../lib/api";
import { useData } from "../lib/data";

const fallbackProfile: InstitutionProfile = {
  organizationName: "示范公募基金",
  portfolioName: "多资产研究组合",
  fundType: "混合型公募基金",
  benchmark: "沪深300 × 50% + 中证全债 × 50%",
  mandate: "在风险预算内追求中长期稳健超额收益",
  decisionHorizon: "季度配置",
  riskPreference: "平衡",
  reportingCycle: "每周",
  focusIndustries: ["半导体", "银行", "新能源"],
  aiOutputStyle: "投委会底稿",
  evidenceStandard: "多源交叉验证",
};

const fallbackConstraints: ConstraintSet = { maxEquity: 65, maxVolatility: 14, maxSingleAsset: 12, minLiquidity: 8 };
const constraintLabels: Record<keyof ConstraintSet, { name: string; help: string }> = {
  maxEquity: { name: "权益仓位上限", help: "组合校准和投委会不得提出超过该值的权益目标" },
  maxVolatility: { name: "风险预算上限", help: "组合校准使用的历史波动率保守上限" },
  maxSingleAsset: { name: "单一资产上限", help: "研究结论映射为组合动作时的集中度边界" },
  minLiquidity: { name: "现金流动性下限", help: "拟议组合必须保留的现金及货币工具比例" },
};

export function ConstraintsPage() {
  const { data, refresh } = useData();
  const [profile, setProfile] = useState<InstitutionProfile>(fallbackProfile);
  const [constraints, setConstraints] = useState<ConstraintSet>(fallbackConstraints);
  const [industries, setIndustries] = useState("半导体, 银行, 新能源");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!data) return;
    setProfile(data.profile);
    setConstraints(data.constraints);
    setIndustries(data.profile.focusIndustries.join(", "));
  }, [data]);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setSaving(true);
    setSaved(false);
    setError("");
    try {
      const nextProfile = { ...profile, focusIndustries: industries.split(/[,，]/).map((item) => item.trim()).filter(Boolean) };
      await Promise.all([api.saveProfile(nextProfile), api.saveConstraints(constraints)]);
      await refresh();
      setSaved(true);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "机构政策保存失败");
    } finally {
      setSaving(false);
    }
  };

  return <form className="page-stack policy-page" onSubmit={submit}>
    <div className="page-title"><div><span className="eyebrow">INSTITUTION POLICY</span><h1>机构政策与约束</h1><p>这里不是装饰性设置：机构画像进入 AI 上下文，风险边界进入组合校准与投委会。</p></div><Badge tone="up"><ShieldCheck size={14}/>全站政策层</Badge></div>

    <Card className="policy-summary-card">
      <div><Building2 size={20}/><span>当前机构</span><strong>{profile.organizationName}</strong><small>{profile.fundType} · {profile.portfolioName}</small></div>
      <div><Target size={20}/><span>业绩基准</span><strong>{profile.benchmark}</strong><small>{profile.decisionHorizon} · {profile.riskPreference}</small></div>
      <div><Sparkles size={20}/><span>AI 研究口径</span><strong>{profile.aiOutputStyle}</strong><small>{profile.evidenceStandard}</small></div>
    </Card>

    <div className="policy-layout">
      <Card>
        <SectionHeader title="机构与产品画像" subtitle="用于控制研究语气、期限、基准与证据标准。"/>
        <div className="policy-form-grid">
          <label><span>机构名称</span><input required value={profile.organizationName} onChange={(event) => setProfile({ ...profile, organizationName: event.target.value })}/></label>
          <label><span>组合 / 产品名称</span><input required value={profile.portfolioName} onChange={(event) => setProfile({ ...profile, portfolioName: event.target.value })}/></label>
          <label><span>基金类型</span><select value={profile.fundType} onChange={(event) => setProfile({ ...profile, fundType: event.target.value })}><option>混合型公募基金</option><option>股票型公募基金</option><option>固收+基金</option><option>FOF / 多资产</option><option>保险资管组合</option><option>机构自营组合</option></select></label>
          <label><span>决策期限</span><select value={profile.decisionHorizon} onChange={(event) => setProfile({ ...profile, decisionHorizon: event.target.value as InstitutionProfile["decisionHorizon"] })}><option>短线跟踪</option><option>季度配置</option><option>年度配置</option></select></label>
          <label className="span-2"><span>业绩基准</span><input required value={profile.benchmark} onChange={(event) => setProfile({ ...profile, benchmark: event.target.value })}/></label>
          <label className="span-2"><span>投资授权 / Mandate</span><textarea required rows={3} value={profile.mandate} onChange={(event) => setProfile({ ...profile, mandate: event.target.value })}/></label>
          <label><span>风险偏好</span><select value={profile.riskPreference} onChange={(event) => setProfile({ ...profile, riskPreference: event.target.value as InstitutionProfile["riskPreference"] })}><option>稳健</option><option>平衡</option><option>进取</option></select></label>
          <label><span>报告周期</span><select value={profile.reportingCycle} onChange={(event) => setProfile({ ...profile, reportingCycle: event.target.value as InstitutionProfile["reportingCycle"] })}><option>每日</option><option>每周</option><option>每月</option></select></label>
          <label><span>AI 输出深度</span><select value={profile.aiOutputStyle} onChange={(event) => setProfile({ ...profile, aiOutputStyle: event.target.value as InstitutionProfile["aiOutputStyle"] })}><option>极简结论</option><option>投委会底稿</option><option>完整研究</option></select></label>
          <label><span>证据标准</span><select value={profile.evidenceStandard} onChange={(event) => setProfile({ ...profile, evidenceStandard: event.target.value as InstitutionProfile["evidenceStandard"] })}><option>公开来源优先</option><option>官方披露优先</option><option>多源交叉验证</option></select></label>
          <label className="span-2"><span>重点跟踪行业</span><input value={industries} onChange={(event) => setIndustries(event.target.value)} placeholder="半导体, 银行, 新能源"/><small>只提高相关信息优先级，不屏蔽其他行业。</small></label>
        </div>
      </Card>

      <Card>
        <SectionHeader title="风险与配置边界" subtitle="保存后立即进入组合校准和投委会的交易前检查。"/>
        <div className="policy-constraint-list">{(Object.keys(constraintLabels) as Array<keyof ConstraintSet>).map((key) => <label key={key}><div><strong>{constraintLabels[key].name}</strong><span>{constraintLabels[key].help}</span></div><div className="number-input"><input type="number" min="0" max="100" step="0.1" value={constraints[key]} onChange={(event) => setConstraints({ ...constraints, [key]: Number(event.target.value) })}/><span>%</span></div></label>)}</div>
        <div className="policy-impact-list"><strong>当前联动范围</strong><span><CheckCircle2 size={14}/>投研驾驶舱：机构名称、重点行业与待复盘事项</span><span><CheckCircle2 size={14}/>宏观 / 一页纸：期限、基准与证据标准进入 AI 上下文</span><span><CheckCircle2 size={14}/>投委会 / 组合校准：权益、波动、集中度与流动性硬约束</span><span><CheckCircle2 size={14}/>投资记忆：按报告周期生成复核日程</span></div>
      </Card>
    </div>

    {error && <div className="form-error">{error}</div>}
    <div className="policy-savebar"><div><strong>{saved ? "机构政策已更新" : "保存后不会改写历史决策"}</strong><span>历史记忆保留当时的参数，新研究使用最新政策。</span></div><Button type="submit" disabled={saving}>{saving ? <Spinner label="正在保存"/> : <><Save size={16}/>{saved ? "已保存" : "保存机构政策"}</>}</Button></div>
  </form>;
}
